import React from 'react';
import { Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PublicIcon from '@mui/icons-material/Public';
import ViewListIcon from '@mui/icons-material/ViewList';
import { logTopNavButtonClicked } from '../services/analytics';
import { buildPeCatalogueHref } from '../utils/searchState';

// ---------------------------------------------------------------------------
// ProjectNavButtons — shared top-of-page navigation grid.
//
// Renders eight tile-style buttons in a single 4-col grid so every
// destination in the Project Essentials surface is one tap away from
// any page. Tiles have an accent-coloured icon on the left and a
// dark label on the right, sitting on a white surface card with a
// hairline border (matches the marketing-app reference design).
//
// Pass `active` so the tile matching the current page renders in a
// filled accent style and its click is a no-op — gives users
// constant orientation while letting them hop between the other
// destinations from any page.
// ---------------------------------------------------------------------------

export type ProjectNavTarget =
  | 'upcoming'
  | 'all-projects'
  | 'commercial'
  | 'highlights'
  | 'edm'
  | 'seminars'
  | 'videos'
  | 'calendar'
  | 'international';

type NavButton = {
  id: ProjectNavTarget;
  label: string;
  icon: React.ReactNode;
};

// Single flat list — order matches the reference screenshot row-by-row.
const NAV_BUTTONS: NavButton[] = [
  { id: 'upcoming', label: 'Upcoming GLS / Enbloc', icon: <EventAvailableIcon fontSize="small" /> },
  { id: 'highlights', label: 'eBooks', icon: <MenuBookIcon fontSize="small" /> },
  { id: 'edm', label: 'EDM/VVIP', icon: <ConfirmationNumberOutlinedIcon fontSize="small" /> },
  { id: 'videos', label: 'Videos', icon: <SmartDisplayOutlinedIcon fontSize="small" /> },
  { id: 'all-projects', label: 'Resi Projects', icon: <ViewListIcon fontSize="small" /> },
  { id: 'commercial', label: 'Commercial / Industrial', icon: <ApartmentIcon fontSize="small" /> },
  { id: 'international', label: 'International', icon: <PublicIcon fontSize="small" /> },
  { id: 'calendar', label: 'Calendar', icon: <CalendarMonthIcon fontSize="small" /> },
];

// Centralised destination map.
const NAV_ROUTES: Partial<Record<ProjectNavTarget, string>> = {
  upcoming: '/project-essential/upcoming-projects',
  'all-projects': '/project-essential/all-projects',
  commercial: '/project-essential/commercial-industrial',
  highlights: '/project-essential/highlights-ebook',
  edm: '/project-essential/project-edm',
  seminars: '/project-essential/seminars-events',
  videos: '/project-essential/videos',
  calendar: '/project-essential/project-calendar',
  international: '/project-essential/international',
};

export default function ProjectNavButtons({ active }: { active?: ProjectNavTarget }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (id: ProjectNavTarget) => {
    if (id === active) return;
    logTopNavButtonClicked(id);
    const route = NAV_ROUTES[id];
    if (route) navigate(buildPeCatalogueHref(route, location.search));
  };

  const buttonSxFor = (id: ProjectNavTarget) =>
    id === active ? activeButtonSx : baseButtonSx;

  return (
    <Box sx={gridSx}>
      {NAV_BUTTONS.map((b) => (
        <Button
          key={b.id}
          variant="text"
          onClick={() => handleClick(b.id)}
          startIcon={b.icon}
          sx={buttonSxFor(b.id)}
          aria-current={b.id === active ? 'page' : undefined}
        >
          {b.label}
        </Button>
      ))}
    </Box>
  );
}

// 4 columns at every breakpoint — matches the reference design and
// keeps the 2-row 8-tile shape stable across phone and desktop.
const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: { xs: 1, sm: 1.25 },
} as const;

// Tile style: white surface card, hairline border, accent-coloured
// icon + dark label.
//
// Layout switches at the `sm` breakpoint: on phones the icon sits
// above the label (vertical stack, centred) so the full label has
// the entire tile width to itself — 4-col grid + horizontal layout
// truncates words like "Commercial" / "EDM/VVIP" on narrow viewports.
// From `sm` upward there's enough room for the original
// icon-on-the-left, label-on-the-right arrangement.
const baseButtonSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  letterSpacing: '0.01em',
  borderRadius: 1.5,
  py: { xs: 1.25, sm: 1.25 },
  px: { xs: 0.5, sm: 1 },
  fontSize: { xs: '0.72rem', sm: '0.85rem' },
  lineHeight: 1.15,
  // Stack icon above label on phones; revert to the standard
  // horizontal layout from `sm` upward. `minWidth: 0` lets long
  // labels truncate cleanly inside the grid cell either way.
  flexDirection: { xs: 'column', sm: 'row' } as const,
  justifyContent: { xs: 'center', sm: 'flex-start' },
  alignItems: 'center',
  textAlign: { xs: 'center', sm: 'left' } as const,
  gap: { xs: 0.5, sm: 0 },
  minWidth: 0,
  minHeight: { xs: 72, sm: 0 },
  bgcolor: 'var(--color-surface)',
  color: 'var(--color-text-strong)',
  // Accent ring around every tile so the nav row reads as a single
  // themed unit. The hover state thickens it slightly via the soft
  // background tint rather than changing the border colour.
  border: '1px solid var(--color-accent)',
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    color: 'var(--color-accent)',
    // Zero horizontal margin when stacked vertically so the icon
    // sits dead-centre above the label; restore the standard
    // right-margin from `sm` upward.
    mr: { xs: 0, sm: 0.75 },
    ml: 0,
    mb: { xs: 0.25, sm: 0 },
  },
  '&:hover': {
    bgcolor: 'var(--color-accent-soft)',
    borderColor: 'var(--color-accent)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'var(--color-text-muted)',
    bgcolor: 'var(--color-surface)',
    // Keep the accent ring on disabled tiles so the row stays
    // visually uniform — only the text/icon dim out.
    border: '1px solid var(--color-accent)',
    opacity: 0.55,
    '& .MuiButton-startIcon': { color: 'var(--color-text-muted)' },
  },
};

// Active tile: filled accent so the user always knows which page
// they're currently on. Click is a no-op (handled in `handleClick`).
const activeButtonSx = {
  ...baseButtonSx,
  bgcolor: 'var(--color-accent)',
  color: '#fff',
  borderColor: 'var(--color-accent)',
  cursor: 'default',
  '& .MuiButton-startIcon': {
    color: '#fff',
    mr: { xs: 0, sm: 0.75 },
    ml: 0,
    mb: { xs: 0.25, sm: 0 },
  },
  '&:hover': {
    bgcolor: 'var(--color-accent)',
    borderColor: 'var(--color-accent)',
    boxShadow: 'none',
  },
};
